// Weerpictogrammen (0026; docs/DESIGN.md §8.WE.5): eigen tekeningen in assets/weather
// (scripts/weather-art.mjs), net als de andere beeldbestanden nooit in de JS-bundle: alleen id's en
// een URL. Kinderpictogrammen per advies (altijd hetzelfde beeld voor hetzelfde ding), weersymbolen
// met een dag- en nachtvariant (geen zon in het nachtgezicht) en beelden voor de veiligheidsband.
// Twee sets met dezelfde bestandsnamen (docs/ux/themas.md): kleurrijk in assets/weather (thema
// Kinderen) en de rustige lijnset in assets/weather-clean (thema Volwassen). WeatherImg kiest.

import { artBase } from './art.js';

/** Adviezen met een kinderpictogram (KID_ITEM_ORDER in @amumin/domain/weather). */
export const WEATHER_ITEM_IDS = [
  'thick_coat',
  'raincoat',
  'coat',
  'boots',
  'hat_gloves',
  'sweater',
  'sunscreen',
  'sun_hat',
  'water_bottle',
  'shorts',
  'umbrella',
] as const;

export const WEATHER_SYMBOL_IDS = [
  'clear',
  'partly_cloudy',
  'cloudy',
  'fog',
  'drizzle',
  'rain',
  'heavy_rain',
  'sleet',
  'snow',
  'thunder',
] as const;

/** Verschijnselen van een waarschuwing → beeld in de band (nooit een uitroepteken). */
export const WEATHER_SAFETY_IDS = [
  'wind',
  'thunder',
  'slippery',
  'cold',
  'heat',
  'rain',
  'snow',
  'fog',
] as const;

/** Alle bestanden in assets/weather (zonder .svg). */
export const WEATHER_ART_FILES: ReadonlyArray<string> = [
  ...WEATHER_ITEM_IDS.map((id) => `item-${id}`),
  ...WEATHER_SYMBOL_IDS.map((id) => `sym-${id}`)
    .filter((f) => f !== 'sym-clear' && f !== 'sym-partly_cloudy')
    .concat([
      'sym-clear-day',
      'sym-clear-night',
      'sym-partly_cloudy-day',
      'sym-partly_cloudy-night',
    ]),
  ...WEATHER_SAFETY_IDS.map((id) => `safe-${id}`),
];

/** Bestandsnaam (zonder .svg) van het kinderpictogram bij een advies; null = geen kinderding. */
export function weatherItemFile(code: string): string | null {
  return (WEATHER_ITEM_IDS as readonly string[]).indexOf(code) >= 0 ? `item-${code}` : null;
}

/** Bestandsnaam van een weersymbool, met dag/nacht voor helder en half bewolkt. Onbekend → bewolkt. */
export function weatherSymbolFile(symbol: string, isDay: boolean): string {
  const s = (WEATHER_SYMBOL_IDS as readonly string[]).indexOf(symbol) >= 0 ? symbol : 'cloudy';
  return s === 'clear' || s === 'partly_cloudy'
    ? `sym-${s}-${isDay ? 'day' : 'night'}`
    : `sym-${s}`;
}

/** Bestandsnaam bij een waarschuwing (verschijnsel); 'ice' = glad, onbekend → mist. */
export function weatherSafetyFile(phenomenon: string): string {
  const id =
    phenomenon === 'ice'
      ? 'slippery'
      : (WEATHER_SAFETY_IDS as readonly string[]).indexOf(phenomenon) >= 0
        ? phenomenon
        : 'fog';
  return `safe-${id}`;
}

/** Kleurrijk bestand (thema Kinderen). */
export function weatherArtUrl(file: string): string {
  return `${artBase()}weather/${file}.svg`;
}

/**
 * Rustige lijntekening (thema Volwassen, assets/weather-clean, gebouwd door
 * scripts/build-own-art.mjs): de groep met id p, via <use> zodat de lijn currentColor volgt.
 */
export function weatherCleanUrl(file: string): string {
  return `${artBase()}weather-clean/${file}.svg#p`;
}

/** Kinderpictogram bij een advies; null als het geen kinderding is. */
export function weatherItemUrl(code: string): string | null {
  const file = weatherItemFile(code);
  return file ? weatherArtUrl(file) : null;
}

/** Weersymbool, met dag/nacht voor helder en half bewolkt. Onbekend → bewolkt. */
export function weatherSymbolUrl(symbol: string, isDay: boolean): string {
  return weatherArtUrl(weatherSymbolFile(symbol, isDay));
}

/** Beeld bij een waarschuwing (verschijnsel); 'ice' = glad, 'other' → wind-neutraal bewolkt. */
export function weatherSafetyUrl(phenomenon: string): string {
  return weatherArtUrl(weatherSafetyFile(phenomenon));
}
